import { useEffect, useState, type ComponentProps } from "react";
import { Spinner } from "@/components/ui/spinner";
import { CheckCircle2Icon, XIcon } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@clerk/react";
import { CreatorAction } from "@/types/types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Frame,
  FrameFooter,
  FrameHeader,
  FramePanel,
  FrameTitle,
  FrameCount,
} from "@/components/ui/frame";
import { useAmyDeepLink } from "@/hooks/use-amy-deep-link";
import { useDashboardStore, useEveActionsStore } from "@/stores";
import { statusAccentStyle } from "@/constants/statuses";
import EmailThreadFrame from "./EmailThreadFrame";
import { InfoRow } from "@/components/creators-table/phase/blocks";
import ConfirmActionDialog from "./ConfirmActionDialog";

/** Numbered amendments, one separated row each (dialog + "already approved" frames). */
function AmendmentRows({ amendments }: { amendments: string[] }) {
  return (
    <div className="flex flex-col divide-y text-sm">
      {amendments.map((a, i) => (
        <div key={i} className="flex items-start gap-3 px-4 py-3">
          <span className="text-xs text-muted-foreground tabular-nums pt-0.5">{i + 1}.</span>
          <span>{a}</span>
        </div>
      ))}
    </div>
  );
}

/** One amendment per non-empty line — the shape the approve route stores. */
const toAmendments = (text: string) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

/**
 * Amy-v2 contract-change task: a creator asked to change contract terms (in
 * an email, or as their DocuSeal decline reason). Amy drafted the amendment
 * wording and holds the creator — nothing went out. Approve (optionally
 * after editing the wording) issues a new contract with an "Amendments to
 * the Creator Agreement" page; Decline keeps the standard contract. Both run
 * NATIVELY against the amy contract-change routes, and this click is the
 * only way any wording reaches a contract.
 */
export default function ContractChangeAction({
  action,
  emailThread,
  threadLoading,
}: {
  action: CreatorAction;
  emailThread: ComponentProps<typeof EmailThreadFrame>["emailThread"];
  threadLoading: boolean;
}) {
  const { getToken } = useAuth();
  const openAmy = useAmyDeepLink();
  const { approveContractChange, declineContractChange, isLoading } = useEveActionsStore();
  const change = action.contract_change;
  const proposal = (change?.proposed_amendments ?? []).join("\n");
  const [wording, setWording] = useState(proposal);
  useEffect(() => setWording(proposal), [action._id, proposal]);

  const inForce = action.amendments_in_force ?? [];
  const amendments = toAmendments(wording);
  // Same test the server runs (team_edited): any line removed, reworded or added.
  const edited = amendments.join("\n") !== toAmendments(proposal).join("\n");
  const requested = change?.requested_at
    ? format(new Date(change.requested_at), "MMM d, yyyy 'at' h:mm a")
    : null;

  const removeTaskRow = () =>
    useDashboardStore.setState((state) => ({
      tasks: state.tasks.filter((task) => task._id !== action._id),
    }));

  const approve = async () => {
    const token = await getToken();
    if (!token || amendments.length === 0) return false;
    const ok = await approveContractChange(action._id, amendments, token);
    if (ok) removeTaskRow();
    return ok;
  };

  const decline = async () => {
    const token = await getToken();
    if (!token) return false;
    const ok = await declineContractChange(action._id, token);
    if (ok) removeTaskRow();
    return ok;
  };

  if (!change) return null;

  return (
    <div
      className="flex flex-col items-start gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250"
      // Every accent below (alert, quote border, focus ring) paints in the
      // "contract-change" STATUS_MAP color.
      style={statusAccentStyle("contract-change")}
    >
      <p className="text-sm text-muted-foreground">
        Amy drafted the wording below. @{action.username} is on hold: nothing goes out until you
        approve or decline.
      </p>

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>
            {change.source === "decline" ? "Their reason for declining in DocuSeal" : "What they asked"}
          </FrameTitle>
        </FrameHeader>
        <FramePanel className="flex flex-col p-0 overflow-hidden">
          <blockquote className="whitespace-pre-wrap border-l-2 border-(--status-accent)/60 m-4 pl-3 text-sm">
            {change.creator_ask}
          </blockquote>
          <div className="flex flex-col divide-y border-t">
            {change.ask_summary && <InfoRow label="In short">{change.ask_summary}</InfoRow>}
            {requested && <InfoRow label="Asked">{requested}</InfoRow>}
          </div>
        </FramePanel>
      </Frame>

      {inForce.length > 0 && (
        <Frame className="w-full">
          <FrameHeader>
            <FrameTitle>Already approved for this creator</FrameTitle>
          </FrameHeader>
          <FramePanel className="p-0 overflow-hidden">
            <AmendmentRows amendments={inForce} />
          </FramePanel>
        </Frame>
      )}

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>Amendment wording{edited ? " (edited)" : " (Amy's draft)"}</FrameTitle>
        </FrameHeader>
        <FramePanel className="relative p-0 border-0">
          <Textarea
            value={wording}
            onChange={(e) => setWording(e.target.value)}
            maxLength={10 * 1500}
            className="min-h-28 resize-none pb-12 focus-visible:ring-(--status-accent)/20 focus-visible:border-(--status-accent)"
          />
          <div className="absolute bottom-2 right-2 flex gap-2">
            <ConfirmActionDialog
              trigger={
                <Button
                  variant="warning-ghost"
                  size="sm"
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner /> : <XIcon />}
                  Decline
                </Button>
              }
              title="Decline this contract change?"
              description={`Amy tells @${action.username} the standard contract stays as is and re-sends the signing link.`}
              confirmLabel="Decline change"
              confirmIcon={<XIcon />}
              confirmVariant="warning"
              isLoading={isLoading}
              onConfirm={decline}
            />
            <ConfirmActionDialog
              trigger={
                <Button
                  variant="neutral"
                  size="sm"
                  disabled={isLoading || amendments.length === 0}
                >
                  {isLoading ? <Spinner /> : <CheckCircle2Icon />}
                  Approve
                </Button>
              }
              title="Approve this contract change?"
              description={`@${action.username} gets a new contract with ${amendments.length === 1 ? "this amendment" : "these amendments"} to initial; the old signing link stops working.${edited ? " You edited Amy's draft, so her email says what's in and that the rest stays standard." : ""}`}
              confirmLabel="Approve and send"
              confirmIcon={<CheckCircle2Icon />}
              isLoading={isLoading}
              onConfirm={approve}
            >
              <Frame>
                <FrameHeader>
                  <FrameTitle>{amendments.length === 1 ? "Amendment" : <>Amendments<FrameCount>{amendments.length}</FrameCount></>}</FrameTitle>
                </FrameHeader>
                <FramePanel className="p-0">
                  <AmendmentRows amendments={amendments} />
                </FramePanel>
              </Frame>
            </ConfirmActionDialog>
          </div>
        </FramePanel>
        <FrameFooter className="flex items-center justify-between gap-2">
          <span>One amendment per line. Edit or delete lines before approving.</span>
          <span
            onClick={() =>
              openAmy(
                `Let's discuss @${action.username}'s contract change request on campaign "${action.campaign_name}" (campaign_creator_id ${action._id}, campaign_id ${action.campaign_id}).`
              )
            }
            className="shrink-0 text-foreground underline cursor-pointer hover:decoration-2 underline-offset-2"
          >
            Discuss with Amy
          </span>
        </FrameFooter>
      </Frame>

      {change.source === "reply" && (
        <div className="w-full">
          <EmailThreadFrame action={action} emailThread={emailThread} threadLoading={threadLoading} />
        </div>
      )}
    </div>
  );
}
